/** Área 1 — Dados da venda: cliente, vendedor, forma de pagamento, depósito, data e tabela. */
import type { SalesOrder } from "@cafegestor/shared";
import { useEffect, useRef, type Ref } from "react";
import { Field, Input, Select } from "@/components/ui";
import { useOpenLoads } from "../../cargas/lib";
import { usePaymentMethods } from "../../compras/lib";
import { WarehouseSelect } from "../../estoque/pickers";
import { useSalesContext } from "../lib";
import { CustomerPicker, SalespersonPicker } from "../pickers";
import type { SaleHeader } from "./draft";

export function SaleHeaderFields({
  header,
  onChange,
  order,
  customerRef,
  readOnly,
  onCustomerSelected,
  noLoad = false,
}: {
  header: SaleHeader;
  onChange: (patch: Partial<SaleHeader>) => void;
  order: SalesOrder | null;
  customerRef?: Ref<HTMLInputElement>;
  readOnly?: boolean;
  onCustomerSelected?: () => void;
  /** v7: Venda Direta — venda da fábrica, nunca da rota (sem campo de carga) */
  noLoad?: boolean;
}) {
  const methods = usePaymentMethods();
  const context = useSalesContext();
  const isNew = order === null;
  const loads = useOpenLoads(isNew && !noLoad);
  // vendedor restrito: o pedido é sempre dele (a API ignora outra escolha)
  const restricted = context.data?.restrictedSalespersonId ?? null;
  const routeSale = header.loadId !== "";
  // novo pedido de vendedor restrito com carga aberta: a venda vai para a carga dele
  const ownLoad = restricted ? loads.data?.find((l) => l.salespersonId === restricted) : undefined;
  const applied = useRef(false);
  useEffect(() => {
    if (!isNew || noLoad || applied.current || !ownLoad || header.loadId) return;
    applied.current = true;
    onChange({ loadId: ownLoad.id });
  }, [isNew, noLoad, ownLoad, header.loadId, onChange]);
  const disabled = readOnly ?? false;
  const method = methods.data?.items.find((m) => m.id === header.paymentMethodId);
  const priceList = order?.priceListName ?? "Definida ao salvar";
  const showLoad = isNew && !noLoad && (loads.data?.length ?? 0) > 0;
  // com o campo Carga, três por linha (2+2+2); sem ele, forma e depósito dividem a linha
  const wide = showLoad ? "min-w-0 @4xl:col-span-2" : "min-w-0 @4xl:col-span-3";
  // A grade acompanha a largura REAL da área (container query), não a da janela: dentro do
  // assistente, com o resumo ao lado, a área é mais estreita que a tela. Uma coluna no
  // celular, duas a partir de ~36rem e três a partir de ~56rem; os rótulos ficam no topo
  // (items-start) e os textos de ajuda abaixo do campo, sem empurrar os vizinhos.
  return (
    <div className="@container">
      <div className="grid grid-cols-1 items-start gap-x-4 gap-y-4 @xl:grid-cols-2 @4xl:grid-cols-6">
        <label className="block min-w-0 text-sm @4xl:col-span-3" htmlFor="venda-cliente">
          <span className="font-medium text-foreground">Cliente</span>
          <div className="mt-1.5">
            <CustomerPicker
              id="venda-cliente"
              {...(customerRef !== undefined ? { inputRef: customerRef } : {})}
              value={header.customerId}
              label={header.customerLabel}
              disabled={disabled}
              onChange={(id, label) => onChange({ customerId: id, customerLabel: label })}
              {...(onCustomerSelected ? { onSelected: () => onCustomerSelected() } : {})}
            />
          </div>
          <span className="mt-1.5 block text-[13px] leading-snug text-muted-foreground">
            Trocar o cliente reaplica a tabela de preços dele
          </span>
        </label>
        <label className="block min-w-0 text-sm @4xl:col-span-3" htmlFor="venda-vendedor">
          <span className="font-medium text-foreground">Vendedor</span>
          <div className="mt-1.5">
            <SalespersonPicker
              id="venda-vendedor"
              value={header.salespersonId}
              label={header.salespersonLabel}
              disabled={disabled || restricted !== null || routeSale}
              onChange={(id, label) => onChange({ salespersonId: id, salespersonLabel: label })}
            />
          </div>
          <span className="mt-1.5 block text-[13px] leading-snug text-muted-foreground">
            {restricted !== null
              ? "Seus pedidos ficam sempre com você"
              : "Vazio: o vendedor do cadastro do cliente"}
          </span>
        </label>
        <div className={wide}>
          <Field label="Forma de pagamento">
            <Select
              value={header.paymentMethodId}
              title={method ? method.name : isNew ? "A do cadastro do cliente" : undefined}
              disabled={disabled}
              onChange={(ev) => onChange({ paymentMethodId: ev.target.value })}
            >
              <option value="">{isNew ? "A do cadastro do cliente" : "—"}</option>
              {/* forma do pedido inativada depois: continua visível (a aprovação recusa) */}
              {order?.paymentMethodId &&
                order.paymentMethodName &&
                methods.data &&
                !methods.data.items.some((m) => m.id === order.paymentMethodId) && (
                  <option value={order.paymentMethodId}>{order.paymentMethodName} (inativa)</option>
                )}
              {methods.data?.items.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.generatesReceivable ? " (a prazo)" : " (à vista)"}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {showLoad && (
          <div className="min-w-0 @4xl:col-span-2">
            <Field label="Carga (venda da rota)">
              <Select
                value={header.loadId}
                disabled={disabled}
                onChange={(ev) => onChange({ loadId: ev.target.value })}
              >
                <option value="">Não é venda da rota</option>
                {loads.data?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.number} — {l.salespersonName}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        )}
        <div className={wide}>
          <Field
            label="Depósito de saída"
            {...(routeSale
              ? {
                  hint: `Estoque do vendedor${order?.loadNumber ? ` (carga ${order.loadNumber})` : ""}`,
                }
              : {})}
          >
            <WarehouseSelect
              value={header.warehouseId}
              disabled={disabled || routeSale}
              excludeVendor
              {...(isNew ? { allOption: "Padrão (fábrica)" } : {})}
              onChange={(v) => onChange({ warehouseId: v })}
            />
          </Field>
        </div>
        <div className="min-w-0 @4xl:col-span-2">
          <Field label="Data do pedido">
            <Input
              type="date"
              value={header.orderDate}
              disabled={disabled}
              onChange={(ev) => onChange({ orderDate: ev.target.value })}
            />
          </Field>
        </div>
        <div
          className={
            showLoad ? "min-w-0 @xl:col-span-2 @4xl:col-span-4" : "min-w-0 @4xl:col-span-4"
          }
        >
          <Field label="Tabela de preço" hint="Vem do cliente (válida hoje): só para conferência">
            <Input readOnly tabIndex={-1} value={priceList} title={priceList} />
          </Field>
        </div>
        <div className="min-w-0 @xl:col-span-2 @4xl:col-span-6">
          <Field label="Observações">
            <Input
              value={header.notes}
              disabled={disabled}
              onChange={(ev) => onChange({ notes: ev.target.value })}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}
